import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';

import { AppRouteBody } from '@/components/AppRouteBody';
import { seedExportConfig } from '@/components/export/configs/seed.export.config';
import {
  type SeedKey,
  UniversalExportModal,
} from '@/components/export/universal';
import { Loader } from '@/components/Loader';
import { NoteModal } from '@/components/modals';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { FeatureLock } from '@/components/ui/FeatureLock';
import { SearchBar } from '@/components/ui/SearchBar';
import { createContactSupportButton } from '@/components/ui/utils';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useMy } from '@/hooks/useMy';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { ImportSeedsModal } from '@/sections/seeds/components/modals/ImportSeedsModal';
import { SeedRemoveTag } from '@/sections/seeds/components/modals/SeedRemoveTag';
import { SeedTag } from '@/sections/seeds/components/modals/SeedTag';
import { SeedsActions } from '@/sections/seeds/components/SeedsActions';
import { SeedsFilters } from '@/sections/seeds/components/SeedsFilters';
import { SeedsHeader } from '@/sections/seeds/components/SeedsHeader';
import { SeedsModals } from '@/sections/seeds/components/SeedsModals';
import { SeedsTable } from '@/sections/seeds/components/SeedsTable';
import {
  useSeedData,
  useSeedFilters,
  useSeedsActions,
  useSeedSearch,
  useSeedSelection,
} from '@/sections/seeds/hooks';
import { useSeedCommentHandlers } from '@/sections/seeds/hooks/actions';
import { SeedType } from '@/sections/seeds/types';
import { seedUrlParams } from '@/sections/seeds/utils/seedUrlParams';
import { useGlobalState } from '@/state/global.state';
import { Seed, SeedStatus } from '@/types';

export const Seeds = () => {
  const queryClient = useQueryClient();

  // Use centralized feature lock for web applications tab
  const {
    locked: isWebApplicationsLocked,
    lockTitle: webAppsLockTitle,
    lockDescription: webAppsLockDescription,
    featureName: webAppsFeatureName,
    isLoading: webAppsLockLoading,
  } = useFeatureLock('seeds.webApplications');

  const [type, setType] = useState<SeedType>(() => {
    const urlFilters = seedUrlParams.read();
    return urlFilters.type || 'domain';
  });

  // Filter state management with hybrid persistence (localStorage + URL)
  const {
    filters: filter,
    setFilters: setFilter,
    setSort,
    resetFilters,
  } = useSeedFilters(type);

  // Search with debouncing for header SearchBar
  const { searchTerm, setSearchTerm } = useSeedSearch(
    filter.search || '',
    debouncedTerm => {
      setFilter(prev => ({
        ...prev,
        search: debouncedTerm,
      }));
    }
  );

  const [seedForComment, setSeedForComment] = useState<Seed | null>(null);

  // Coordinated data fetching (seeds + counts + preseed count + totalCount)
  const {
    seeds: apiData,
    error,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    hasNextPage,
    isAllEmpty,
    isFilteredEmpty,
    refetchSeeds,
    isAccountEmpty,
  } = useSeedData(filter);

  // Fetch total seed count from database via statistics (matches Assets pattern)
  const { data: seedStatistics } = useMy(
    {
      resource: 'statistic',
      query: '#class##seed##now#',
    },
    {
      staleTime: 60000, // Cache for 60 seconds
      refetchInterval: false, // Override default 30s polling
    }
  );

  // Compute total count from statistics response
  const totalDatabaseCount = useMemo(() => {
    if (!seedStatistics) return 0;
    return seedStatistics.reduce(
      (sum, item) => sum + (item.data?.count || 0),
      0
    );
  }, [seedStatistics]);

  const { modal } = useGlobalState();

  // Selection state management
  const { selectedKeys, setSelectedKeys, selectedSeeds, clearSelection } =
    useSeedSelection(apiData || [], filter);

  // Invalidation function for seed queries
  const invalidateSeeds = () => {
    queryClient.invalidateQueries({
      predicate: query => {
        const queryKey = query.queryKey as string[];
        return queryKey[0] === 'MY' && queryKey[1] === 'seed';
      },
      refetchType: 'active',
    });
  };

  // Coordinator hook - composes modal state + action handlers
  const { modalState, modalHandlers, handlers } = useSeedsActions({
    selectedSeeds,
    selectedRowsCount: selectedKeys.length,
    invalidateSeeds,
    setConfirmStatus: () => {},
  });

  // Comment handler hook for direct access to handleAddComment
  const { handleAddComment } = useSeedCommentHandlers();

  const handleComment = async (comment: string) => {
    // Use seedForComment if set (single row comment), otherwise use selectedSeeds (bulk comment)
    const seedsToComment = seedForComment ? [seedForComment] : selectedSeeds;

    if (seedsToComment.length === 0) {
      console.warn('[Seeds] handleComment called with no seeds selected');
      return;
    }

    // Directly call handleAddComment with the correct seeds
    // Note: seedType param is not used (prefixed with _) - function uses type guard internally
    const seedType = type === 'preseed' ? 'domain' : type; // TypeScript requires non-preseed type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleAddComment(seedsToComment as any, comment, seedType);
    invalidateSeeds();
    clearSelection();
    setSeedForComment(null);
  };

  // Force table remount on empty → data transition (fixes rendering bug)
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    apiData?.length || 0
  );

  // Generate stable key based on filter/sort state to force remount on changes
  // This resets scroll position and clears stale virtualizer state
  // Note: search is excluded - debounced at API level, shouldn't trigger remounts
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        filters: filter.advancedFilters,
        sort: filter.sort,
        type: filter.type,
      }),
    [filter.advancedFilters, filter.sort, filter.type]
  );

  // Combine with existing tableKey for empty→data transitions
  const combinedTableKey = `${tableKey}-${filterKey}`;

  // Show centered loader only when:
  // 1. Seeds data is loading AND
  // 2. We don't know yet if account has data OR we know it's empty
  const shouldShowCenteredLoader =
    (status === 'pending' && isAccountEmpty !== false) ||
    (type === 'webapplication' && webAppsLockLoading);

  // Keyboard action handler
  function handleKeyboardAction(code: string, data: Seed[]) {
    const keyMap = {
      KeyA: SeedStatus.Accept,
      KeyR: SeedStatus.Reject,
      KeyD: SeedStatus.Delete,
      KeyF: SeedStatus.Freeze,
    };
    const status = keyMap[code as keyof typeof keyMap];
    if (status) {
      modalState.confirmation.open(status, data);
    }
  }

  // Confirmation modal handler
  const handleConfirmStatusChange = async () => {
    if (!modalState.confirmation.data) return;
    const { status } = modalState.confirmation.data;

    // Use the appropriate status handler from coordinator
    switch (status) {
      case SeedStatus.Accept:
        await handlers.handleApprove();
        break;
      case SeedStatus.Reject:
        await handlers.handleReject();
        break;
      case SeedStatus.Freeze:
        await handlers.handleFreeze();
        break;
      case SeedStatus.Delete:
        await handlers.handleDelete();
        break;
    }

    clearSelection();
    modalHandlers.closeConfirmationModal();
  };

  return (
    <AppRouteBody title="Seeds">
      {shouldShowCenteredLoader ? (
        <div className="flex h-full items-center justify-center">
          <Loader isLoading type="spinner" />
        </div>
      ) : (
        <>
          {/* Conditionally render tabs only when account has seeds */}
          {isAccountEmpty === false && (
            <div className="mb-4">
              <SeedsHeader activeTab={type} onTabChange={setType} />
            </div>
          )}

          {/* Content area with optional lock overlay */}
          <div className="relative flex flex-1 flex-col overflow-hidden">
            <FeatureLock
              locked={type === 'webapplication' && isWebApplicationsLocked}
              title={webAppsLockTitle}
              description={webAppsLockDescription}
              action={createContactSupportButton(webAppsFeatureName)}
            >
              {/* Unified Controls Container: Filters + Selection Bar */}
              {!isAllEmpty && (
                <TableControlsContainer
                  data-testid="seeds-controls"
                  filterLabel={true}
                  filters={
                    <SeedsFilters
                      filters={filter}
                      setFilters={setFilter}
                      seedType={type}
                    />
                  }
                  filtersRight={
                    <SearchBar
                      size="sm"
                      className="h-fit w-64"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={setSearchTerm}
                    />
                  }
                  selectionBar={
                    <TableSelectionBar
                      embedded={true}
                      alwaysVisible={true}
                      isVisible={selectedKeys.length > 0}
                      selectedCount={selectedKeys.length}
                      totalCount={apiData?.length || 0}
                      hasNextPage={hasNextPage}
                      totalDatabaseCount={totalDatabaseCount}
                      resourceLabel={`${type}s`}
                      emptySelectionMessage={`Select ${type}s to perform bulk actions`}
                      actions={
                        <SeedsActions
                          seedType={type}
                          selectedSeeds={selectedSeeds}
                          selectedRowsCount={selectedKeys.length}
                          modalHandlers={modalHandlers}
                          actionHandlers={handlers}
                          onClearSelection={clearSelection}
                        />
                      }
                    />
                  }
                />
              )}

              {/* Unified table - works for ALL types including preseed */}
              <SeedsTable
                key={`${type}-${combinedTableKey}`}
                seedType={type}
                data={apiData}
                status={status}
                error={error}
                fetchNextPage={fetchNextPage}
                isFetching={isFetching}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                sort={
                  filter.sort && {
                    orderBy: filter.sort.orderBy,
                    descending: filter.sort.descending,
                    onSort: setSort,
                  }
                }
                selectedRows={selectedKeys}
                onSelectionChange={setSelectedKeys}
                onKeyboardAction={handleKeyboardAction}
                onWebCrawl={seedKey => {
                  modal.selectUserAgent.onAssetKeysChange([seedKey]);
                }}
                onCommentClick={seed => {
                  setSeedForComment(seed);
                  modalHandlers.openCommentModal();
                }}
                isAllEmpty={isAllEmpty}
                onResetFilters={resetFilters}
                onAddSeed={
                  type !== 'preseed'
                    ? modalHandlers.openAddSeedModal
                    : undefined
                }
              />
            </FeatureLock>
          </div>
        </>
      )}
      <SeedsModals
        modalState={modalState}
        seedType={type}
        seedForComment={seedForComment}
        onConfirmStatusChange={handleConfirmStatusChange}
        onRefetchSeeds={refetchSeeds}
      />
      <NoteModal
        open={modalState.comment.isOpen}
        onClose={() => {
          setSeedForComment(null);
          modalHandlers.closeCommentModal();
        }}
        onSubmit={handleComment}
        resourceType="Seed"
        mode="simple"
        selectedCount={seedForComment ? 1 : selectedKeys.length}
        presentation="modal"
      />
      <SeedTag
        open={modalState.tag.isOpen}
        onClose={modalHandlers.closeTagModal}
        seedKeys={selectedKeys}
        onSuccess={() => {
          clearSelection();
          refetchSeeds();
        }}
      />
      <SeedRemoveTag
        open={modalState.removeTag.isOpen}
        onClose={modalHandlers.closeRemoveTagModal}
        seedKeys={selectedKeys}
        onSuccess={() => {
          clearSelection();
          refetchSeeds();
        }}
      />
      <UniversalExportModal
        config={seedExportConfig}
        open={modalState.export.isOpen}
        onClose={modalHandlers.closeExportModal}
        selectedRows={
          selectedSeeds.map(seed => seed.key) as unknown as readonly SeedKey[]
        }
        onClearSelection={() => {
          clearSelection();
          modalHandlers.closeExportModal();
        }}
      />
      <ImportSeedsModal
        open={modalState.import.isOpen}
        onClose={modalHandlers.closeImportModal}
        onImport={() => {
          refetchSeeds();
          modalHandlers.closeImportModal();
        }}
      />
    </AppRouteBody>
  );
};

// Re-export types needed by other components
export type { SeedFilter } from '@/hooks/useSeeds';
